/**
 * ============================================================================
 * AuditLog 模块 API 工厂函数
 * ============================================================================
 * 
 * 📋 文件职责：
 * - 定义审计日志模块的所有 API 请求方法
 * - 通过工厂模式实现请求客户端的依赖注入
 * - 提供完整的 TypeScript 类型安全
 * 
 * 🎯 核心概念：工厂模式
 * - 工厂函数：createAuditLogApi(client) - 接收请求客户端，返回 API 方法集合
 * - 依赖注入：不依赖具体的请求实现（axios、uni.request 等）
 * - 平台无关：Web 端和 App 端都可以使用，只需提供不同的适配器
 * 
 * 💡 设计理念：
 * - "给我一个能发送请求的工具，我就能为你生成所有 API 方法"
 * - 请求客户端是"工具"，API 方法是"产品"，工厂函数是"生产线"
 * 
 * 📖 使用方式：
 * 
 * **Web 端（Vben Admin + axios）**：
 * ```typescript
 * // 1. 创建适配器（让 Vben 的 requestClient 符合 IRequestClient 接口）
 * import { requestClient } from '#/api/request'
 * import type { IRequestClient } from '@bayes-mes/shared/api/core/client'
 * 
 * const webAdapter: IRequestClient = {
 *   get: (url, config) => requestClient.get(url, config).then(res => res.data),
 *   post: (url, data, config) => requestClient.post(url, data, config).then(res => res.data),
 *   // ... 其他方法
 * }
 * 
 * // 2. 调用工厂函数，传入适配器
 * import { createAuditLogApi } from '@bayes-mes/shared/api/audit-log/factory'
 * export const auditLogApi = createAuditLogApi(webAdapter)
 * 
 * // 3. 使用生成的 API
 * const result = await auditLogApi.getAuditLogList({ page: 1 })
 * ```
 * 
 * **App 端（uni-app X）**：
 * ```typescript
 * // 1. 创建适配器（让 uni.request 符合 IRequestClient 接口）
 * const uniAdapter: IRequestClient = {
 *   get: (url, config) => new Promise((resolve, reject) => {
 *     uni.request({
 *       url,
 *       method: 'GET',
 *       data: config?.params,
 *       success: (res) => resolve(res.data.data),
 *       fail: reject
 *     })
 *   }),
 *   // ... 其他方法
 * }
 * 
 * // 2. 调用工厂函数
 * import { createAuditLogApi } from '@bayes-mes/shared/api/audit-log/factory'
 * export const auditLogApi = createAuditLogApi(uniAdapter)
 * ```
 * 
 * ⚠️ 注意事项：
 * - **请求客户端接口**：所有工厂函数都使用 `api/core/client.ts` 中定义的 `IRequestClient` 接口
 * - **不要重复定义接口**：从 `../core/client` 导入，不要在每个模块中重新定义
 * - **适配器职责**：适配器负责将平台特定的请求客户端转换为符合 `IRequestClient` 的对象
 * - **响应数据解包**：适配器需要处理响应数据的解包（如 `res.data.data`）
 * - **类型安全**：所有方法都有完整的 TypeScript 类型定义
 * - **注释详细**：每个方法都有详细的中文注释，包括使用场景、参数、返回值、数据流向
 * 
 * 🔄 更新时机：
 * - 后端新增 API 方法
 * - 后端修改参数或响应结构
 * - 后端修改路由路径
 * 
 * ✅ 验证方式：
 * - 确保所有 Controller 的 public 方法都已映射
 * - 确保所有类型导入正确
 * - 确保 URL 路径与后端路由一致
 * - 运行 `npm run type-check` 验证 TypeScript 编译
 * 
 * @module api/audit-log/factory
 * @description 审计日志模块 API 工厂函数
 * @generated AI-assisted
 * @author AI Assistant
 * @created 2025-10-18
 * @last-updated 2025-10-18
 * 
 * @change-log
 * - 2025-10-18: 初始创建，实现所有审计日志查询方法
 * ============================================================================
 */

// ===== 导入类型定义 =====
import type {
  AuditLog,                      // 审计日志主实体
  AuditLogQueryParams,           // 列表查询参数
  AuditStatsQueryParams,         // 统计查询参数
  AuditTrendQueryParams,         // 趋势查询参数
  ObjectHistoryQueryParams,      // 对象历史查询参数
  ExportAuditLogParams,          // 导出参数
  AuditStatistics,               // 统计数据
  AuditTrend,                    // 趋势数据
  ExportResult,                  // 导出结果
  ObjectHistoryResult,           // 对象历史结果
} from '../../types/audit-log'

// ===== 导入通用类型 =====
import type { PaginationResult } from '../../types/api'

// ===== 导入统一的请求客户端接口 =====
// ⚠️ 重要：所有工厂函数都从这里导入接口，不要重复定义！
import type { IRequestClient } from '../core/client'

// ===== 导入 API 路由定义 =====
import { API_ROUTES } from '../routes'

// ===== 导出工厂函数 =====
/**
 * 创建审计日志模块的 API 方法集合
 * 
 * 这是一个工厂函数，它接收一个"请求客户端"作为参数，
 * 返回一个包含所有审计日志相关 API 方法的对象。
 * 
 * @param client - 请求客户端，必须实现 IRequestClient 接口
 *   - 可以是 Web 端的 axios 适配器
 *   - 可以是 App 端的 uni.request 适配器
 *   - 可以是测试用的 Mock 客户端
 * 
 * @returns API 方法集合对象，包含所有审计日志相关的请求方法
 * 
 * 为什么这样设计？
 * - 解耦：API 逻辑与具体的请求实现分离
 * - 复用：同一套 API 定义可以在 Web 和 App 端使用
 * - 测试：可以注入 Mock 客户端进行单元测试
 */
export function createAuditLogApi(client: IRequestClient) {
  return {
    // ===== 基础查询操作 =====
    
    /**
     * 获取审计日志列表（分页）
     * 
     * 使用场景：
     * - 在审计日志管理页面的主列表中使用
     * - 支持分页、搜索、多条件筛选功能
     * - 用户可以根据操作人、模块、操作类型、时间范围等条件查询
     * 
     * @param params - 查询参数对象（可选）
     *   - page?: number - 页码，从 1 开始，默认 1
     *     示例：page: 1（第一页），page: 2（第二页）
     *   - per_page?: number - 每页数量，默认 20
     *     示例：per_page: 20（每页20条）
     *   - user_id?: number - 按用户ID筛选
     *     示例：user_id: 123（只查看ID为123的用户的操作）
     *   - username?: string - 按用户名筛选（模糊匹配）
     *     示例：username: '张三'（搜索用户名包含"张三"的记录）
     *   - module?: string - 按模块筛选
     *     示例：module: '物料管理'（只查看物料管理模块的操作）
     *   - action?: string - 按操作类型筛选
     *     示例：action: 'create'（只查看创建操作）
     *   - subject_type?: string - 按操作对象类型筛选
     *     示例：subject_type: 'App\\Models\\Material'（只查看物料相关操作）
     *   - keyword?: string - 关键词搜索（搜索描述或对象名称）
     *     示例：keyword: '删除了'（搜索包含"删除了"的操作）
     *   - start_date?: string - 开始日期（格式：YYYY-MM-DD）
     *     示例：start_date: '2025-10-01'（查看10月1日之后的记录）
     *   - end_date?: string - 结束日期（格式：YYYY-MM-DD）
     *     示例：end_date: '2025-10-31'（查看10月31日之前的记录）
     * 
     * @returns Promise<PaginationResult<AuditLog>> 分页结果对象
     *   返回的数据结构：
     *   {
     *     items: AuditLog[],         // 审计日志列表数组
     *     total: number,             // 总记录数
     *     page: number,              // 当前页码
     *     pageSize: number,          // 每页数量
     *     totalPages: number         // 总页数
     *   }
     * 
     * 数据流向（完整过程）：
     * 1. 用户操作：用户在审计日志页面上选择筛选条件，点击"查询"按钮
     * 2. 前端调用：组件调用 auditLogApi.getAuditLogList({ page: 1, module: '物料管理' })
     * 3. 适配器处理：适配器将参数转换为 HTTP 请求
     *    - Web端：axios 发送 GET 请求
     *    - App端：uni.request 发送 GET 请求
     * 4. 网络传输：发送到后端 GET /api/v1/audit/logs?page=1&module=物料管理
     * 5. 后端处理：
     *    - 路由匹配到 AuditController@index 方法
     *    - 验证查询参数
     *    - 查询数据库，应用筛选条件和分页
     *    - 通过 SystemAuditLogResource 格式化数据（snake_case → camelCase）
     * 6. 响应返回：后端返回 JSON 格式的分页数据
     * 7. 适配器解包：适配器提取 response.data.data（实际数据）
     * 8. 前端接收：组件收到 PaginationResult<AuditLog> 类型的数据
     * 9. 页面更新：使用数据更新表格显示
     * 
     * 代码示例：
     * ```typescript
     * // 示例1：获取第一页（使用默认参数）
     * const page1 = await auditLogApi.getAuditLogList()
     * console.log(page1.items)  // 审计日志数组
     * console.log(page1.total)  // 总记录数
     * 
     * // 示例2：按模块筛选
     * const materialLogs = await auditLogApi.getAuditLogList({
     *   module: '物料管理',
     *   page: 1,
     *   per_page: 20
     * })
     * 
     * // 示例3：按时间范围查询
     * const recentLogs = await auditLogApi.getAuditLogList({
     *   start_date: '2025-10-01',
     *   end_date: '2025-10-18'
     * })
     * 
     * // 示例4：组合查询
     * const result = await auditLogApi.getAuditLogList({
     *   username: '张三',
     *   action: 'delete',
     *   start_date: '2025-10-01',
     *   page: 1,
     *   per_page: 10
     * })
     * ```
     * 
     * 错误处理：
     * - 404：资源不存在（路由错误或后端未实现）
     * - 422：参数验证失败（page 不是数字等）
     * - 500：服务器内部错误（数据库查询失败等）
     */
    getAuditLogList(params?: AuditLogQueryParams) {
      return client.get<PaginationResult<AuditLog>>(API_ROUTES.audit.list, { params })
    },
    
    /**
     * 获取审计日志详情
     * 
     * 使用场景：
     * - 在审计日志详情页面显示完整信息
     * - 查看操作的详细变更内容（oldValues、newValues）
     * - 分析某次操作的完整上下文
     * 
     * @param id - 审计日志的唯一标识符（主键）
     *   示例：id: 12345（获取 ID 为 12345 的审计日志）
     * 
     * @returns Promise<AuditLog> 单个审计日志对象
     *   返回的数据结构包含完整的审计信息：
     *   - 操作人信息：userId, username, name
     *   - 操作信息：module, action, subjectType, subjectId, subjectName
     *   - 变更数据：oldValues, newValues（JSON对象）
     *   - 额外信息：extraData, ipAddress, userAgent, requestId
     *   - 时间信息：createdAt
     * 
     * 数据流向：
     * 1. 用户点击审计日志列表中的某一行，或直接访问详情页
     * 2. 前端调用：auditLogApi.getAuditLogDetail(12345)
     * 3. 发送请求：GET /api/v1/audit/logs/12345
     * 4. 后端处理：AuditController@show 方法
     *    - 验证 ID 参数
     *    - 从数据库查询指定 ID 的记录
     *    - 如果不存在，返回 404
     *    - 记录查看操作（调用 AuditService::logView）
     *    - 通过 SystemAuditLogResource 格式化数据
     * 5. 返回数据：单个 AuditLog 对象
     * 6. 前端显示：在详情页展示数据，支持对比 oldValues 和 newValues
     * 
     * 代码示例：
     * ```typescript
     * // 获取指定审计日志详情
     * const detail = await auditLogApi.getAuditLogDetail(12345)
     * console.log(detail.action)      // 操作类型
     * console.log(detail.module)      // 模块名称
     * console.log(detail.oldValues)   // 修改前的数据
     * console.log(detail.newValues)   // 修改后的数据
     * 
     * // 在详情页组件中使用
     * const { id } = useRoute().params
     * const logData = await auditLogApi.getAuditLogDetail(Number(id))
     * 
     * // 展示变更对比
     * if (logData.action === 'update') {
     *   Object.keys(logData.newValues).forEach(key => {
     *     console.log(`${key}: ${logData.oldValues[key]} → ${logData.newValues[key]}`)
     *   })
     * }
     * ```
     * 
     * 错误处理：
     * - 404：审计日志不存在
     * - 403：无权限访问该审计日志
     * - 422：ID 格式错误（不是数字）
     * 
     * ⚠️ 注意：
     * - 查看审计日志详情本身也会被记录到审计日志中
     * - oldValues 和 newValues 可能很大，需要注意性能
     */
    getAuditLogDetail(id: number) {
      return client.get<AuditLog>(API_ROUTES.audit.detail(id))
    },
    
    // ===== 统计分析 =====
    
    /**
     * 获取审计统计数据
     * 
     * 使用场景：
     * - 在审计日志概览页面显示统计卡片
     * - 分析系统操作趋势和用户活跃度
     * - 生成审计报告
     * 
     * @param params - 查询参数（可选）
     *   - start_date?: string - 开始日期（格式：YYYY-MM-DD），默认30天前
     *   - end_date?: string - 结束日期（格式：YYYY-MM-DD），默认今天
     * 
     * @returns Promise<AuditStatistics> 统计数据对象
     *   包含以下统计维度：
     *   - totalCount: 总记录数（全部时间）
     *   - todayCount: 今日记录数
     *   - weekCount: 本周记录数
     *   - monthCount: 本月记录数
     *   - moduleStats: 各模块操作统计（TOP 10）
     *   - actionStats: 各操作类型统计
     *   - topUsers: 活跃用户 TOP 10
     * 
     * 数据流向：
     * 1. 用户进入审计日志概览页面
     * 2. 前端调用：auditLogApi.getAuditStats()
     * 3. 发送请求：GET /api/v1/audit/stats
     * 4. 后端处理：AuditController@stats 方法
     *    - 查询总记录数、今日、本周、本月记录数
     *    - 按模块分组统计（GROUP BY module）
     *    - 按操作类型分组统计（GROUP BY action）
     *    - 查询活跃用户 TOP 10（GROUP BY user_id, ORDER BY count DESC）
     *    - 组装统计数据
     * 5. 返回数据：AuditStatistics 对象
     * 6. 前端显示：渲染统计卡片、图表
     * 
     * 代码示例：
     * ```typescript
     * // 获取默认统计（最近30天）
     * const stats = await auditLogApi.getAuditStats()
     * console.log(`今日操作：${stats.todayCount}`)
     * console.log(`本周操作：${stats.weekCount}`)
     * console.log(`本月操作：${stats.monthCount}`)
     * 
     * // 渲染模块统计图表
     * const moduleChartData = {
     *   labels: stats.moduleStats.map(m => m.module),
     *   data: stats.moduleStats.map(m => m.count)
     * }
     * 
     * // 渲染活跃用户列表
     * stats.topUsers.forEach(user => {
     *   console.log(`${user.name} (${user.username}): ${user.count}次操作`)
     * })
     * 
     * // 自定义时间范围
     * const customStats = await auditLogApi.getAuditStats({
     *   start_date: '2025-10-01',
     *   end_date: '2025-10-18'
     * })
     * ```
     * 
     * 错误处理：
     * - 422：日期格式错误
     * - 500：数据库查询失败
     */
    getAuditStats(params?: AuditStatsQueryParams) {
      return client.get<AuditStatistics>(API_ROUTES.audit.stats, { params })
    },
    
    /**
     * 获取审计趋势数据
     * 
     * 使用场景：
     * - 在审计日志概览页面显示趋势图表
     * - 分析操作频率的时间变化
     * - 对比不同模块的活跃度趋势
     * 
     * @param params - 查询参数（可选）
     *   - days?: number - 天数，默认30天
     *     示例：days: 7（最近7天），days: 90（最近90天）
     * 
     * @returns Promise<AuditTrend> 趋势数据对象
     *   包含：
     *   - dates: 日期数组（格式：MM-DD）
     *   - counts: 对应日期的总操作次数
     *   - byModule: 按模块分类的趋势数据
     * 
     * 数据流向：
     * 1. 用户查看审计趋势图表
     * 2. 前端调用：auditLogApi.getAuditTrend({ days: 30 })
     * 3. 发送请求：GET /api/v1/audit/trend?days=30
     * 4. 后端处理：AuditController@trend 方法
     *    - 计算开始日期（now - days）
     *    - 按天分组统计总操作数（GROUP BY DATE(created_at)）
     *    - 查询所有模块列表
     *    - 为每个模块按天分组统计
     *    - 组装趋势数据
     * 5. 返回数据：AuditTrend 对象
     * 6. 前端显示：渲染折线图/柱状图
     * 
     * 代码示例：
     * ```typescript
     * // 获取最近30天趋势
     * const trend = await auditLogApi.getAuditTrend({ days: 30 })
     * 
     * // 渲染总操作趋势图
     * const totalTrendChart = {
     *   xAxis: trend.dates,        // ['10-01', '10-02', ...]
     *   yAxis: trend.counts        // [120, 135, ...]
     * }
     * 
     * // 渲染各模块趋势对比图
     * const moduleLines = Object.entries(trend.byModule).map(([module, data]) => ({
     *   name: module,
     *   data: data.counts
     * }))
     * 
     * // 最近7天趋势
     * const weekTrend = await auditLogApi.getAuditTrend({ days: 7 })
     * ```
     * 
     * 错误处理：
     * - 422：days 参数不是数字
     * - 500：数据库查询失败
     */
    getAuditTrend(params?: AuditTrendQueryParams) {
      return client.get<AuditTrend>(API_ROUTES.audit.trend, { params })
    },
    
    // ===== 辅助查询 =====
    
    /**
     * 获取可用模块列表
     * 
     * 使用场景：
     * - 在筛选条件下拉框中显示所有模块选项
     * - 动态生成模块筛选器
     * 
     * @returns Promise<string[]> 模块名称数组
     *   示例：['物料管理', '生产管理', '库存管理', ...]
     * 
     * 数据流向：
     * 1. 页面初始化时加载模块列表
     * 2. 前端调用：auditLogApi.getAuditModules()
     * 3. 发送请求：GET /api/v1/audit/modules
     * 4. 后端处理：AuditController@modules 方法
     *    - 查询 system_audit_logs 表的 module 字段（DISTINCT）
     *    - 排序返回
     * 5. 返回数据：字符串数组
     * 6. 前端显示：填充到下拉框
     * 
     * 代码示例：
     * ```typescript
     * // 获取模块列表
     * const modules = await auditLogApi.getAuditModules()
     * 
     * // 渲染下拉框
     * const options = modules.map(module => ({
     *   label: module,
     *   value: module
     * }))
     * ```
     */
    getAuditModules() {
      return client.get<string[]>(API_ROUTES.audit.modules)
    },
    
    /**
     * 获取可用操作类型列表
     * 
     * 使用场景：
     * - 在筛选条件下拉框中显示所有操作类型选项
     * - 动态生成操作类型筛选器
     * 
     * @returns Promise<string[]> 操作类型数组
     *   示例：['create', 'update', 'delete', 'view', ...]
     * 
     * 数据流向：
     * 1. 页面初始化时加载操作类型列表
     * 2. 前端调用：auditLogApi.getAuditActions()
     * 3. 发送请求：GET /api/v1/audit/actions
     * 4. 后端处理：AuditController@actions 方法
     *    - 查询 system_audit_logs 表的 action 字段（DISTINCT）
     *    - 排序返回
     * 5. 返回数据：字符串数组
     * 6. 前端显示：填充到下拉框，结合 AUDIT_ACTION_LABELS 显示中文
     * 
     * 代码示例：
     * ```typescript
     * import { AUDIT_ACTION_LABELS } from '@bayes-mes/shared/types/audit-log'
     * 
     * // 获取操作类型列表
     * const actions = await auditLogApi.getAuditActions()
     * 
     * // 渲染下拉框（显示中文）
     * const options = actions.map(action => ({
     *   label: AUDIT_ACTION_LABELS[action] || action,
     *   value: action
     * }))
     * ```
     */
    getAuditActions() {
      return client.get<string[]>(API_ROUTES.audit.actions)
    },
    
    // ===== 导入导出 =====
    
    /**
     * 导出审计日志
     * 
     * 使用场景：
     * - 导出符合条件的审计日志为 CSV 文件
     * - 支持按查询条件筛选导出
     * - 用于审计报告生成、合规检查
     * 
     * @param params - 导出参数（筛选条件）
     *   - username?: string - 按用户名筛选
     *   - module?: string - 按模块筛选
     *   - action?: string - 按操作类型筛选
     *   - start_date?: string - 开始日期
     *   - end_date?: string - 结束日期
     * 
     * @returns Promise<ExportResult> 导出结果对象
     *   包含：
     *   - data: CSV数据（二维数组）
     *   - count: 导出记录数
     *   - filename: 建议的文件名
     * 
     * 数据流向：
     * 1. 用户点击"导出"按钮
     * 2. 前端调用：auditLogApi.exportAuditLogs({ module: '物料管理' })
     * 3. 发送请求：POST /api/v1/audit/logs/export
     * 4. 后端处理：AuditController@export 方法
     *    - 应用筛选条件查询数据（限制10000条）
     *    - 格式化为CSV格式
     *    - 记录导出操作到审计日志
     *    - 返回CSV数据
     * 5. 返回数据：ExportResult 对象
     * 6. 前端处理：将CSV数据转换为文件并下载
     * 
     * 代码示例：
     * ```typescript
     * // 导出所有审计日志
     * const result = await auditLogApi.exportAuditLogs()
     * 
     * // 导出指定条件的审计日志
     * const filtered = await auditLogApi.exportAuditLogs({
     *   module: '物料管理',
     *   action: 'delete',
     *   start_date: '2025-10-01',
     *   end_date: '2025-10-18'
     * })
     * 
     * // 处理导出数据
     * console.log(`导出了 ${result.count} 条记录`)
     * 
     * // 转换为Blob并下载
     * const csvContent = result.data.map(row => row.join(',')).join('\n')
     * const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' })
     * const url = URL.createObjectURL(blob)
     * const link = document.createElement('a')
     * link.href = url
     * link.download = result.filename
     * link.click()
     * URL.revokeObjectURL(url)
     * ```
     * 
     * 错误处理：
     * - 422：参数格式错误
     * - 403：无导出权限
     * - 500：导出失败（数据过大等）
     * 
     * ⚠️ 注意：
     * - 导出数量限制为10000条，防止内存溢出
     * - 导出操作本身会被记录到审计日志
     * - CSV文件包含BOM头（\ufeff）以支持Excel正确显示中文
     */
    exportAuditLogs(params?: ExportAuditLogParams) {
      return client.post<ExportResult>(API_ROUTES.audit.export, params)
    },
    
    // ===== 特殊查询 =====
    
    /**
     * 获取对象变更历史
     * 
     * 使用场景：
     * - 查看某个具体对象（如某个物料、某个订单）的所有变更记录
     * - 追溯对象的完整生命周期
     * - 审计特定对象的操作历史
     * 
     * @param params - 查询参数（必填）
     *   - subject_type: string - 对象类型（必填）
     *     示例：'App\\Models\\Material'
     *   - subject_id: number - 对象ID（必填）
     *     示例：123
     * 
     * @returns Promise<ObjectHistoryResult> 变更历史结果
     *   包含：
     *   - items: 审计日志列表（按时间倒序）
     *   - total: 总记录数
     * 
     * 数据流向：
     * 1. 用户在对象详情页点击"查看变更历史"
     * 2. 前端调用：auditLogApi.getObjectHistory({
     *      subject_type: 'App\\Models\\Material',
     *      subject_id: 123
     *    })
     * 3. 发送请求：GET /api/v1/audit/object-history?subject_type=...&subject_id=123
     * 4. 后端处理：AuditController@objectHistory 方法
     *    - 验证参数（必填）
     *    - 查询指定对象的所有审计日志
     *    - 按时间倒序排序
     *    - 通过 SystemAuditLogResource 格式化
     * 5. 返回数据：ObjectHistoryResult 对象
     * 6. 前端显示：时间线视图，展示对象的所有变更
     * 
     * 代码示例：
     * ```typescript
     * // 查看物料ID=123的变更历史
     * const history = await auditLogApi.getObjectHistory({
     *   subject_type: 'App\\Models\\Material',
     *   subject_id: 123
     * })
     * 
     * console.log(`共有 ${history.total} 条变更记录`)
     * 
     * // 渲染时间线
     * history.items.forEach(log => {
     *   console.log(`${log.createdAt}: ${log.name} ${log.description}`)
     *   if (log.action === 'update') {
     *     // 显示变更详情
     *     Object.keys(log.newValues).forEach(key => {
     *       console.log(`  ${key}: ${log.oldValues[key]} → ${log.newValues[key]}`)
     *     })
     *   }
     * })
     * 
     * // 查看订单变更历史
     * const orderHistory = await auditLogApi.getObjectHistory({
     *   subject_type: 'App\\Models\\SalesOrder',
     *   subject_id: 456
     * })
     * ```
     * 
     * 错误处理：
     * - 422：参数缺失或格式错误
     * - 404：对象不存在
     * - 500：查询失败
     * 
     * ⚠️ 注意：
     * - subject_type 必须是完整的类名（包含命名空间）
     * - 返回的是所有相关记录，不分页
     * - 适合对象变更不频繁的场景
     */
    getObjectHistory(params: ObjectHistoryQueryParams) {
      return client.get<ObjectHistoryResult>(API_ROUTES.audit.objectHistory, { params })
    },
  }
}

